import React, { useEffect, useState, useCallback } from 'react';
import { Layers, Check, X, Eye, EyeOff, Pencil, ArrowUp, ArrowDown } from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';

const PLAN_COLORS = {
  free: 'bg-gray-100 text-gray-700',
  starter: 'bg-blue-100 text-blue-700',
  professional: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700',
};

const STATUS_COLORS = {
  live: 'bg-green-100 text-green-700',
  coming_soon: 'bg-gray-100 text-gray-500',
};

const CATEGORIES = ['all', 'core', 'communication', 'documents', 'ai', 'integrations', 'finance', 'analytics', 'crm'];

const PLAN_OPTIONS = ['free', 'starter', 'professional', 'enterprise'];
const STATUS_OPTIONS = ['live', 'coming_soon'];
const VISIBILITY_OPTIONS = ['visible', 'hidden'];

export default function Features() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editModal, setEditModal] = useState(null);
  const [confirmHide, setConfirmHide] = useState(null);

  const fetchFeatures = useCallback(async () => {
    try {
      const res = await api.get('/platform/features');
      setFeatures(res.data.features || []);
    } catch {
      toast.error('Failed to load features');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFeatures(); }, [fetchFeatures]);

  const updateFeature = async (id, data) => {
    try {
      const res = await api.patch(`/platform/features/${id}`, data);
      const updated = res.data.feature;
      setFeatures(prev => prev.map(f => f.id === id ? updated : f));
      return updated;
    } catch {
      toast.error('Failed to update');
      return null;
    }
  };

  const toggleStatus = async (feature) => {
    const newStatus = feature.status === 'live' ? 'coming_soon' : 'live';
    const result = await updateFeature(feature.id, { status: newStatus });
    if (result) toast.success(newStatus === 'live' ? 'Published' : 'Set to Coming Soon');
  };

  const toggleVisibility = async (feature) => {
    if (feature.visibility === 'visible') {
      setConfirmHide(feature);
    } else {
      const result = await updateFeature(feature.id, { visibility: 'visible' });
      if (result) toast.success('Feature is now visible');
    }
  };

  const confirmHideAction = async () => {
    if (!confirmHide) return;
    const result = await updateFeature(confirmHide.id, { visibility: 'hidden' });
    if (result) toast.success('Feature hidden from all tenant dashboards');
    setConfirmHide(null);
  };

  const moveFeature = async (feature, direction) => {
    const sorted = [...features].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex(f => f.id === feature.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const ids = sorted.map(f => f.id);
    [ids[idx], ids[swapIdx]] = [ids[swapIdx], ids[idx]];

    try {
      await api.post('/platform/features/reorder', { ordered_ids: ids });
      fetchFeatures();
    } catch {
      toast.error('Failed to reorder');
    }
  };

  const saveEdit = async (id, data) => {
    const result = await updateFeature(id, data);
    if (result) {
      toast.success('Feature updated');
      setEditModal(null);
    }
  };

  const filtered = filter === 'all' ? features : features.filter(f => f.category === filter);
  const sorted = [...filtered].sort((a, b) => a.sort_order - b.sort_order);

  const stats = {
    total: features.length,
    live: features.filter(f => f.status === 'live').length,
    coming_soon: features.filter(f => f.status === 'coming_soon').length,
    hidden: features.filter(f => f.visibility === 'hidden').length,
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-gray-200 rounded" />
          <div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-48 bg-gray-200 rounded-xl" />)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Layers className="w-6 h-6 text-gray-400" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Features</h1>
          <p className="text-sm text-gray-500">Control what tenants see and can access</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-xs text-gray-500">Total</div>
        </div>
        <div className="bg-white rounded-lg border border-green-200 p-3 text-center">
          <div className="text-xl font-bold text-green-600">{stats.live}</div>
          <div className="text-xs text-gray-500">Live</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-xl font-bold text-gray-500">{stats.coming_soon}</div>
          <div className="text-xs text-gray-500">Coming Soon</div>
        </div>
        <div className="bg-white rounded-lg border border-red-200 p-3 text-center">
          <div className="text-xl font-bold text-red-500">{stats.hidden}</div>
          <div className="text-xs text-gray-500">Hidden</div>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {CATEGORIES.map(cat => {
          const count = cat === 'all' ? features.length : features.filter(f => f.category === cat).length;
          if (count === 0 && cat !== 'all') return null;
          return (
            <button key={cat} onClick={() => setFilter(cat)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium capitalize transition-colors ${
                filter === cat ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {cat === 'all' ? 'All' : cat.replace('_', ' ')} ({count})
            </button>
          );
        })}
      </div>

      {/* Feature cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sorted.map(feature => {
          const isHidden = feature.visibility === 'hidden';
          const isComingSoon = feature.status === 'coming_soon';
          const borderColor = isHidden ? 'border-l-red-400' : isComingSoon ? 'border-l-gray-400' : 'border-l-green-400';
          return (
            <div key={feature.id} className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden border-l-4 ${borderColor} ${isHidden ? 'opacity-60' : ''}`}>
              <div className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{feature.name}</h3>
                    <p className="text-[11px] text-gray-400 font-mono">{feature.slug}</p>
                  </div>
                  <span className="text-[10px] text-gray-400 ml-2">#{feature.sort_order}</span>
                </div>

                {/* Description */}
                <p className="text-xs text-gray-500 mb-3 line-clamp-2">{feature.description}</p>

                {/* Badges */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[feature.status]}`}>
                    {feature.status === 'live' ? 'Live' : 'Coming Soon'}
                  </span>
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium ${isHidden ? 'bg-red-100 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
                    {isHidden ? 'Hidden' : 'Visible'}
                  </span>
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium capitalize ${PLAN_COLORS[feature.min_plan]}`}>
                    {feature.min_plan}
                  </span>
                  <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-50 text-gray-500 capitalize">
                    {feature.category}
                  </span>
                </div>

                {feature.route && (
                  <p className="text-[11px] text-gray-400 mb-2">Route: {feature.route}</p>
                )}
              </div>

              {/* Actions */}
              <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center gap-1.5">
                <button onClick={() => toggleStatus(feature)} title={feature.status === 'live' ? 'Set Coming Soon' : 'Set Live'}
                  className={`px-2 py-1 text-[11px] font-medium rounded ${
                    feature.status === 'live' ? 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-100' : 'text-green-700 bg-green-50 border border-green-200 hover:bg-green-100'
                  }`}>
                  {feature.status === 'live' ? 'Coming Soon' : 'Go Live'}
                </button>
                <button onClick={() => toggleVisibility(feature)} title={isHidden ? 'Show feature tile on tenant dashboards' : 'Hides tile only — never deletes tenant data or disables existing configurations'}
                  className={`px-2 py-1 text-[11px] font-medium rounded ${
                    isHidden ? 'text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100' : 'text-red-600 bg-red-50 border border-red-200 hover:bg-red-100'
                  }`}>
                  {isHidden ? <><Eye className="w-3 h-3 inline mr-0.5" />Show</> : <><EyeOff className="w-3 h-3 inline mr-0.5" />Hide</>}
                </button>
                <button onClick={() => setEditModal(feature)} className="px-2 py-1 text-[11px] font-medium text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-100">
                  <Pencil className="w-3 h-3 inline mr-0.5" />Edit
                </button>
                <div className="ml-auto flex gap-0.5">
                  <button onClick={() => moveFeature(feature, 'up')} className="p-1 text-gray-400 hover:text-gray-700"><ArrowUp className="w-3 h-3" /></button>
                  <button onClick={() => moveFeature(feature, 'down')} className="p-1 text-gray-400 hover:text-gray-700"><ArrowDown className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirm Hide Modal */}
      {confirmHide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Hide "{confirmHide.name}"?</h3>
            <p className="text-sm text-gray-500 mb-2">
              Hiding {confirmHide.name} will remove the tile from all tenant dashboards.
              Tenants who already have this feature configured will keep all their data
              and configuration — only the admin panel tile is hidden.
            </p>
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-4">
              This never deletes tenant data or disables existing configurations.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmHide(null)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={confirmHideAction} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Hide Feature</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <EditFeatureModal
          feature={editModal}
          onSave={(data) => saveEdit(editModal.id, data)}
          onClose={() => setEditModal(null)}
        />
      )}
    </div>
  );
}

function EditFeatureModal({ feature, onSave, onClose }) {
  const [form, setForm] = useState({
    name: feature.name || '',
    description: feature.description || '',
    category: feature.category || 'core',
    status: feature.status || 'live',
    visibility: feature.visibility || 'visible',
    min_plan: feature.min_plan || 'free',
    route: feature.route || '',
    icon: feature.icon || '',
    icon_color: feature.icon_color || '',
    icon_bg: feature.icon_bg || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Edit Feature</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
                {CATEGORIES.filter(c => c !== 'all').map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Min Plan</label>
              <select value={form.min_plan} onChange={e => set('min_plan', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
                {PLAN_OPTIONS.map(p => <option key={p} value={p} className="capitalize">{p}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === 'live' ? 'Live' : 'Coming Soon'}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Visibility</label>
              <select value={form.visibility} onChange={e => set('visibility', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
                {VISIBILITY_OPTIONS.map(v => <option key={v} value={v} className="capitalize">{v}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Route</label>
            <input type="text" value={form.route} onChange={e => set('route', e.target.value)} placeholder="/admin/..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Icon</label>
              <input type="text" value={form.icon} onChange={e => set('icon', e.target.value)} placeholder="Database"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Icon Color</label>
              <input type="text" value={form.icon_color} onChange={e => set('icon_color', e.target.value)} placeholder="text-blue-600"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Icon BG</label>
              <input type="text" value={form.icon_bg} onChange={e => set('icon_bg', e.target.value)} placeholder="bg-blue-50"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
